import React, { useEffect, useRef, useState } from 'react';
import { Clock, Video } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
import { trackDemoBooked } from '../lib/analytics';
import { TALK_TO_SALES_URL } from '../lib/links';

// The same calendar every "Talk to sales" control on the site opens. This page
// used to embed a different one, so booking from the header and booking from
// here landed on two different event types.
const CALENDLY_URL = TALK_TO_SALES_URL;
const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

// Calendly's own booking flow replaces the previous fake calendar — a mock
// scheduler on a real "Book a demo" page books nothing.
export function BookDemo() {
  useScreenInit();
  const holder = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Loaded at runtime rather than bundled so a blocked or slow script leaves
    // the page intact instead of failing the build.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CALENDLY_SCRIPT}"]`);
    const init = () => {
      const Calendly = (window as any).Calendly;
      if (!Calendly || !holder.current || holder.current.childElementCount > 0) return;
      Calendly.initInlineWidget({
        url: `${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=ea580c`,
        parentElement: holder.current
      });
      setReady(true);
    };
    if ((window as any).Calendly) {
      init();
      return;
    }
    const script = existing ?? Object.assign(document.createElement('script'), {
      src: CALENDLY_SCRIPT,
      async: true
    });
    script.addEventListener('load', init);
    if (!existing) document.body.appendChild(script);
    return () => script.removeEventListener('load', init);
  }, []);
  // Calendly posts a message once a booking is confirmed inside the iframe —
  // the only way to know the demo was actually booked.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (typeof event.data?.event === 'string' && event.data.event === 'calendly.event_scheduled') {
        trackDemoBooked();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  return <div className="relative min-h-screen overflow-hidden bg-[#1C1510] px-6 py-24" data-id="element-7106" data-mp="38r82pk7w6paa">
      <div className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-50" data-id="element-7107" data-mp="33rv98gmimjyq" />
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#F97316]/[0.08] blur-[120px]" data-id="element-7108" data-mp="33toim3oijiaf" />

      <div className="relative z-10 mx-auto w-full max-w-5xl" data-id="element-7109" data-mp="2tzzb55kfitui">
        <div className="text-center" data-id="element-7110" data-mp="2p4a5ckhtggpr">
          <h1 className="font-title text-4xl font-bold tracking-tight text-white md:text-5xl" data-id="element-7111" data-mp="2p14fh31d5asz">
            Book a Giga demo
          </h1>
          <p className="mx-auto mt-4 max-w-[52ch] text-lg leading-relaxed text-white/60" data-id="element-7112" data-mp="2g20vlwudgrdf">
            Thirty minutes, walked through your own tools — how Giga connects
            them, what it can run for you, and how access stays under control.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/50" data-id="element-7113" data-mp="10l61e7e6drze">
            <span className="inline-flex items-center gap-2" data-id="element-7114" data-mp="19jrehn0voess">
              <Clock className="size-4" data-id="element-7115" data-mp="19j86ay1154ry" />
              30 minutes
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-7116" data-mp="cvbo5opy21em">
              <Video className="size-4" data-id="element-7117" data-mp="guua8nti4ij1" />
              Google Meet link on confirmation
            </span>
          </div>
        </div>

        <div className="mt-10 rounded-3xl border border-white/10 bg-white shadow-2xl" data-id="element-7118" data-mp="lsvrd37da7rv">
          {/* Calendly's inline iframe is sized at 100% of its parent, so the
           parent needs a real height — with only a min-height the iframe
           resolved to a shorter box and the scheduler's own header was clipped
           off the top. */}
          <div ref={holder} className="calendly-holder h-[1180px] w-full overflow-hidden rounded-3xl sm:h-[1000px] lg:h-[760px]" data-id="element-7119" data-mp="20r9rqnkai7x6" />
          {!ready && <p className="px-6 pb-8 text-center text-sm text-neutral-500" data-id="element-7120" data-mp="1vtq10o6eo2oc">
              Loading available times…{' '}
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#EA580C] underline" data-id="element-7121" data-mp="29l4n53vm0jzm">
                Open the scheduler directly
              </a>
            </p>}
        </div>
      </div>
    </div>;
}